// Cart-aware bundle resolution (scenarios 1 & 2).
//
// Shopify's Recommendations API only accepts a single product id, so it cannot
// detect "this product alone → offer the bundle" or "these two products → offer
// the everything set". Those rules are configured theme-side (section blocks)
// and resolved here. The same normalized result can later be produced by Rebuy.

import type {
  BundleRule,
  Cart,
  CartItem,
  PairBundleRule,
  SingleBundleRule,
  UpsellRecommendation,
  UpsellResult,
} from "./types";

/** Cart items excluding the gift-with-purchase line, if any. */
function payingItems(items: CartItem[], giftVariantId?: number): CartItem[] {
  return items.filter(
    (item) =>
      item.id !== giftVariantId && item.properties?._gift_with_purchase !== "true",
  );
}

function matchesTrigger(items: CartItem[], productIds: number[]): CartItem[] {
  return items.filter((item) => productIds.includes(item.product_id));
}

/** Pick the upgrade target paired with the matched trigger product id. */
function targetForTrigger(
  trigger: number[],
  targets: UpsellRecommendation[],
  items: CartItem[],
): UpsellRecommendation[] | null {
  // if every product id in the trigger is in the items, return the targets
  if (items.every(item => trigger.includes(item.product_id))) {
    return targets;
  } 

  return null;
}

function toUpgradeRecommendation(
  target: UpsellRecommendation,
  removeKeys: string[],
): UpsellRecommendation {
  return { ...target, actionType: "upgrade", removeKeys };
}

function resolveSingle(
  rule: SingleBundleRule,
  items: CartItem[],
): UpsellResult | null {
  // Scenario 1 fires only when there is exactly one (paying) product line and
  // it matches this rule's trigger.
  if (items.length >= 3) return null;

  const match = matchesTrigger(items, rule.trigger);
  if (match.length === 0) return null;

  const targets = targetForTrigger(rule.trigger, rule.target, items);
  if (!targets) return null;

  return {
    title: rule.title,
    recommendations: targets.map(target => toUpgradeRecommendation(target, match.map(item => item.key))),
  };
}

/**
 * Returns the most specific matching bundle upgrade for the current cart, or
 * `null` if no bundle rule applies (caller then falls back to cross-sell).
 * Pair rules take precedence over single rules.
 */
export function resolveBundle(
  cart: Cart,
  bundles: BundleRule[],
  giftVariantId?: number,
): UpsellResult | null {
  const items = payingItems(cart.items ?? [], giftVariantId);
  if (items.length === 0 || bundles.length === 0) return null;

  for (const rule of bundles) {
    if (rule.type === "single") {
      const result = resolveSingle(rule, items);
      if (result) return result;
    }
  }

  return null;
}

export { payingItems };
